import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { CoachingParticipantDto, CoachingRecordInput } from "../services/coaching-dtos";
import { getCoachingServices } from "../services/coaching-service-provider";
import { cacheParticipantAssignMembershipReadback, cacheParticipantAttachReadback, cacheParticipantLifecycleReadback, cacheParticipantRemoval } from "./coaching-write-truth-caches";
import { createIdempotencyKey, getMutationSideEffectSummary } from "./useCoachingQueries";

const services = getCoachingServices();

export function useParticipantLifecycleMutations() {
  const queryClient = useQueryClient();

  return {
    sideEffectSummary: {
      update: getMutationSideEffectSummary("participants.update"),
      remove: getMutationSideEffectSummary("participants.removeFromCohort"),
      attachToCohort: getMutationSideEffectSummary("participants.attachToCohort"),
      assignMembership: getMutationSideEffectSummary("participants.assignMembership")
    },
    update: useMutation({
      mutationFn: ({ cohortId, participantId, body }: { cohortId: string; participantId: string; body: CoachingRecordInput }) =>
        services.participants.update(cohortId, participantId, body, createIdempotencyKey(`participants-update:${participantId}`)),
      onSuccess: (participant) => cacheParticipantLifecycleReadback(queryClient, participant)
    }),
    remove: useMutation({
      // F6: admin removes a participant from a cohort; future registrants +
      // calendar events are cancelled server-side via the outbox. The operation
      // returns ok honestly; the caller toasts ONLY on a confirmed remove.
      mutationFn: ({ cohortId, participantId }: { cohortId: string; participantId: string }) =>
        services.participants.removeFromCohort(cohortId, participantId, createIdempotencyKey(`participants-remove:${participantId}`)),
      onSuccess: (result, variables) =>
        cacheParticipantRemoval(queryClient, result, variables.cohortId, variables.participantId)
    }),
    attachToCohort: useMutation({
      mutationFn: ({ cohortId, body }: { cohortId: string; body: CoachingRecordInput }) =>
        services.participants.attachToCohort(cohortId, body, createIdempotencyKey(`participants-attach:${cohortId}`)),
      onSuccess: (result, variables) => cacheParticipantAttachReadback(queryClient, result, variables.cohortId)
    }),
    assignMembership: useMutation({
      mutationFn: ({ cohortId }: { cohortId: string }) =>
        // The live route refuses membership assignment without an explicit
        // { confirm: true } body (400 CO_VALIDATION_FAILED).
        services.participants.assignMembership(cohortId, { confirm: true }, createIdempotencyKey(`participants-assign:${cohortId}`)),
      onSuccess: () => cacheParticipantAssignMembershipReadback(queryClient)
    })
  };
}

export type LiveParticipantUpdate = CoachingParticipantDto;

